import React, { useState, useEffect } from 'react';

// You will need to replace this with your actual API endpoint 
// that securely lists the files from your DigitalOcean Space.
const FILE_LIST_API_URL = "https://faas-tor1-70ca848e.doserverless.co/api/v1/web/fn-847a3952-c11c-434f-b908-f0a2b503c72b/default/ListFiles";

// --- NEW: Define the prefix used by the backend function ---
// This MUST match the DO_SPACE_PREFIX environment variable on your Python function.
const DISPLAY_PREFIX = "Templates/"; 
// -------------------------------------------------------------

// --- NEW: Sorting function (folders first, then alphabetical) ---
const sortNodes = (a, b) => {
    // Folders come before files (isFolder = true sorts first)
    if (a.isFolder && !b.isFolder) return -1;
    if (!a.isFolder && b.isFolder) return 1;
    // Otherwise, sort alphabetically by name
    return a.name.localeCompare(b.name);
};
// -----------------------------------------------------------------


// Function to simulate transforming flat S3 keys into a tree structure
const buildTree = (fileKeys) => {
  const tree = [];
  
  fileKeys.forEach(key => {
    // 1. TRIM THE PREFIX: Remove the leading DISPLAY_PREFIX from the key
    let trimmedKey = key;
    if (key.startsWith(DISPLAY_PREFIX)) {
        trimmedKey = key.substring(DISPLAY_PREFIX.length);
    }
    
    // Skip if the key is empty after trimming 
    if (!trimmedKey) return; 

    // 2. Build the tree structure based on the trimmed key
    const parts = trimmedKey.split('/').filter(p => p.length > 0);
    let currentNode = tree;
    let fullPath = DISPLAY_PREFIX; // Start fullPath with the original prefix for correct S3 path

    parts.forEach((part, index) => {
      // CONSTRUCT FULL PATH: Append the part to the full S3 path
      fullPath += part + (index < parts.length - 1 ? '/' : ''); 
      
      let existingNode = currentNode.find(node => node.name === part);

      if (!existingNode) {
        existingNode = {
          name: part,
          isFolder: index < parts.length - 1,
          path: fullPath, // The actual S3 key used for download/customization
          children: [],
          key: fullPath,
        };
        currentNode.push(existingNode);
        
        // Sort the current level immediately after adding a node
        currentNode.sort(sortNodes);
      }

      currentNode = existingNode.children;
    });
  });
  return tree;
};


// --- Recursive Component to Render the Tree ---
const TreeNode = ({ node, onFileSelect, selectedFile, isExpanded }) => {
  const [expanded, setExpanded] = useState(isExpanded || false);

  const toggleExpand = (e) => {
    e.stopPropagation();
    if (node.isFolder) {
      setExpanded(!expanded);
    }
  };

  const handleSelect = () => {
    if (!node.isFolder) {
      onFileSelect(node.path);
    }
  };
  
  // Using standard emojis for better cross-platform rendering
  const icon = node.isFolder 
    ? (expanded ? '📂' : '📁') 
    : '📄';
  
  const isSelected = selectedFile === node.path && !node.isFolder;

  return (
    <div className="ml-4 cursor-pointer">
      <div 
        onClick={node.isFolder ? toggleExpand : handleSelect}
        className={`flex items-center p-1 rounded-md transition duration-150 
        ${node.isFolder ? 'hover:bg-gray-200' : (isSelected ? 'bg-teal-100 font-semibold' : 'hover:bg-gray-100')}`}
      >
        <span className="mr-2 text-lg">{icon}</span>
        <span className={`${node.isFolder ? 'font-medium text-gray-800' : 'text-sm text-gray-700'}`}>
          {node.name}
        </span>
      </div>
      
      {node.isFolder && expanded && (
        <div className="border-l border-gray-300 ml-2">
          {node.children.map(child => (
            <TreeNode 
              key={child.key} 
              node={child} 
              onFileSelect={onFileSelect} 
              selectedFile={selectedFile} 
            />
          ))}
        </div>
      )}
    </div>
  );
};


// --- Main Component ---
const FileTreeView = ({ customizationFunction, orgName }) => { // <-- ACCEPT NEW PROP
  const [fileTree, setFileTree] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to update the selected file state
  const onFileSelect = (filePath) => { 
    setSelectedFile(filePath);
  };


  // Fetch file keys from API
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await fetch(FILE_LIST_API_URL);
        if (!response.ok) {
          throw new Error("Failed to fetch file list from API.");
        }
        
        const fileKeys = await response.json(); 
        setFileTree(buildTree(fileKeys));

        setLoading(false);
      } catch (err) {
        console.error("File List Fetch Error:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  const handleDownload = () => {
    if (selectedFile) {
      // Execute the working function passed from the parent Dashboard
      customizationFunction(selectedFile); 
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading documents...</div>;
  if (error) return <div className="p-8 text-center text-red-500">Error loading documents: {error}</div>;


  return (
    <div className="flex flex-col h-full bg-white shadow-lg rounded-lg overflow-hidden">
      <header className="p-4 border-b bg-gray-50 flex justify-between items-center">
        <h2 className="text-xl font-bold text-teal-800">
          📚 {orgName} Documents {/* <-- USE PROP HERE */}
        </h2>
        <button
          onClick={handleDownload}
          disabled={!selectedFile}
          className={`px-6 py-2 rounded-full font-semibold transition ${
            selectedFile
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Download/Customize File
        </button>
      </header>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-1">
        {fileTree.length === 0 ? (
          <p className="text-center text-gray-500 pt-10">No documents found in the bucket.</p>
        ) : (
          fileTree.map(node => (
            <TreeNode 
              key={node.key} 
              node={node} 
              onFileSelect={onFileSelect}      
              selectedFile={selectedFile}    
              isExpanded={false} // Start top level closed
            />
          ))
        )}
      </div>

      <footer className="p-3 border-t bg-gray-50 text-sm text-gray-600">
        Selected File: <span className="font-mono text-teal-700 break-all">{selectedFile || "None"}</span>
      </footer>
    </div>
  );
};

export default FileTreeView;
